import { z } from 'zod'
import { BatteryContractSchema, BatteryContractSchemaForSharepoint } from './schemas/contract.schema'
import {
  BatteryCounterpartySchema,
  BatteryCounterpartySchemaForSharepoint,
  vatIdOrTaxNumberRequiredMessage,
} from './schemas/counterparty.schema'
import {
  BatteryAssetFormSchema,
  BatteryAssetSchema,
  BatteryAssetSchemaForSharepoint,
  DUMMY_MALO_PREFIX,
  YearlyPerformanceRowSchema,
} from './schemas/asset.schema'
import {
  FeeDataSchema,
  FeeDataFormSchema,
  FeeDataSchemaForSharepoint,
  YearlyFeeRowSchema,
  YearlyFeeListSchema,
} from './schemas/fee.schema'
import { deepPartial } from './utils/deep-partial'
import { deriveContractYearIndices, deriveContractYearWindows } from './utils/contract-year'
import { OfferStatus } from './offer-status'

export {
  BatteryContractSchema,
  BatteryContractSchemaForSharepoint,
  BatteryCounterpartySchema,
  BatteryCounterpartySchemaForSharepoint,
  BatteryAssetSchema,
  BatteryAssetFormSchema,
  BatteryAssetSchemaForSharepoint,
  DUMMY_MALO_PREFIX,
  YearlyPerformanceRowSchema,
  FeeDataSchema,
  FeeDataFormSchema,
  FeeDataSchemaForSharepoint,
  YearlyFeeRowSchema,
  YearlyFeeListSchema,
  deriveContractYearIndices,
  deriveContractYearWindows,
  OfferStatus,
  vatIdOrTaxNumberRequiredMessage,
}
export type { ContractYearWindow } from './utils/contract-year'

// Single source of truth: maps each form key to its full Zod schema.
export const formSchemas = {
  contractData: BatteryContractSchema,
  counterpartyData: BatteryCounterpartySchema,
  batteryAssetRecord: BatteryAssetSchema,
  feeData: FeeDataSchema,
} satisfies Record<string, z.ZodObject>

export type FormIdentificator = keyof typeof formSchemas
export const FormIdentificator = z.enum(Object.keys(formSchemas) as [FormIdentificator, ...FormIdentificator[]])

export const writeRequestSchema = z.object({
  contractNumber: z.string().min(1),
  formIdentificator: FormIdentificator,
  data: z.looseObject({}),
})

// Deep-partial registry for validating incomplete form saves
export const formSchemaRegistry = Object.fromEntries(
  Object.entries(formSchemas).map(([k, v]) => [k, deepPartial(v)])
) as Record<FormIdentificator, z.ZodType>

export type BatteryData = { [K in FormIdentificator]?: z.infer<(typeof formSchemas)[K]> }

export interface OfferSummary {
  contractNumber: string
  name: string
  createdAt: string
  status: OfferStatus
}

export interface HubspotDeal {
  id: string
  revenue: string
  name: string
  contractNumber: string
  contractStartDate?: string
  contractEndDate?: string
  contractPeriod?: string
  flexPriceModel?: string
  batterySize?: string
  batteryCapacity?: string
  docVersionContract?: string
  productGroups?: string
}

export interface HubspotCompany {
  id: string
  name: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  phone?: string
  email?: string
  website?: string
  postalBox?: string
  state?: string
  country?: string
  taxNumber?: string
  taxId?: string
}

export interface Offer {
  contractNumber: string
  createdAt: string
  status: OfferStatus
  hubspot: {
    deal: HubspotDeal
    company: HubspotCompany
    firedAt: string
  }
  data?: BatteryData
}
